import { Text } from '@mantine/core';
import classes from './bar-distribution.module.css';

export interface DistributionBar {
  /** Drawn under the bar. Kept to a couple of characters. */
  label: string;
  value: number;
  /** Pulls the bar into the accent colour — used to mark where the mass sits. */
  emphasis?: boolean;
}

interface BarDistributionProps {
  bars: DistributionBar[];
  /** Describes the whole chart to a screen reader; the SVG itself is hidden from it. */
  caption: string;
  /** Axis label under the bar labels. */
  axisLabel?: string;
  /**
   * Draw a running total across the bars.
   *
   * Turns "two thirds of the loss is in the first three turns" from a sentence
   * in a caption into something the reader can see.
   */
  cumulative?: boolean;
}

const CHART_HEIGHT = 150;
const BAR_WIDTH = 34;
const BAR_GAP = 10;
const LABEL_BAND = 22;
const VALUE_BAND = 18;

const format = new Intl.NumberFormat('en-GB');

/**
 * A vertical bar distribution, drawn by hand.
 *
 * hive has no charting library and adding one would need its own ADR, which is
 * a lot of ceremony for the single chart-shaped thing on these screens. An SVG
 * with a viewBox scales the same way a charting library's would; the colours
 * come from the CSS module so both colour schemes are covered, which inline
 * fills would not be.
 */
export function BarDistribution({
  bars,
  caption,
  axisLabel,
  cumulative = false,
}: BarDistributionProps) {
  const peak = bars.reduce((max, bar) => Math.max(max, bar.value), 0);
  const width = bars.length * BAR_WIDTH + (bars.length - 1) * BAR_GAP;
  const height = CHART_HEIGHT + LABEL_BAND + VALUE_BAND;
  const total = bars.reduce((sum, bar) => sum + bar.value, 0);

  const runningPoints = bars.reduce<{ x: number; y: number; share: number }[]>(
    (points, bar, index) => {
      const running = (points[index - 1]?.share ?? 0) * total + bar.value;
      const share = total === 0 ? 0 : running / total;
      points.push({
        x: index * (BAR_WIDTH + BAR_GAP) + BAR_WIDTH / 2,
        y: VALUE_BAND + CHART_HEIGHT - share * CHART_HEIGHT,
        share,
      });
      return points;
    },
    [],
  );

  return (
    <div className={classes.wrapper}>
      <svg
        role="img"
        aria-label={caption}
        viewBox={`0 0 ${width} ${height}`}
        className={classes.chart}
        preserveAspectRatio="xMinYMax meet"
      >
        {bars.map((bar, index) => {
          const barHeight =
            peak === 0 ? 0 : Math.max(2, (bar.value / peak) * CHART_HEIGHT);
          const x = index * (BAR_WIDTH + BAR_GAP);
          const y = VALUE_BAND + (CHART_HEIGHT - barHeight);

          return (
            <g key={bar.label}>
              <text
                x={x + BAR_WIDTH / 2}
                y={y - 5}
                textAnchor="middle"
                className={classes.value}
              >
                {format.format(bar.value)}
              </text>
              <rect
                x={x}
                y={y}
                width={BAR_WIDTH}
                height={barHeight}
                rx={3}
                className={bar.emphasis ? classes.barEmphasis : classes.bar}
              />
              <text
                x={x + BAR_WIDTH / 2}
                y={height - 6}
                textAnchor="middle"
                className={classes.label}
              >
                {bar.label}
              </text>
            </g>
          );
        })}

        {cumulative && (
          <>
            <polyline
              points={runningPoints
                .map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`)
                .join(' ')}
              className={classes.cumulativeLine}
            />
            {runningPoints.map((point, index) => (
              <circle
                key={index}
                cx={point.x}
                cy={point.y}
                r={2.5}
                className={classes.cumulativeDot}
              />
            ))}
          </>
        )}
      </svg>
      {axisLabel && (
        <Text size="xs" c="dimmed" ta="center" mt={4}>
          {axisLabel}
        </Text>
      )}
    </div>
  );
}
